import PropTypes from 'prop-types';
import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import CaretDown from 'web-ui-kit/assets/CaretDown';
import { surfaceM, grayMediumM, grayLightM, textDisabledM, primaryHighlightM } from 'web-ui-kit/colors';
import { useOutsideClick } from 'web-ui-kit/hooks';
import { BodyLargeStyles } from 'web-ui-kit/typography';

function _taggedTemplateLiteral(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }

  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  if (typeof Symbol === "undefined" || !(Symbol.iterator in Object(arr))) return;
  var _arr = [];
  var _n = true;
  var _d = false;
  var _e = undefined;

  try {
    for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(n);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }

  return arr2;
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

var _templateObject, _templateObject2, _templateObject3, _templateObject4;
/**
 * Fluid width selection control that allows selecting a single
 * option from a list of strings via a dropdown menu.
 */


function Dropdown(_ref) {
  var _ref$className = _ref.className,
      className = _ref$className === void 0 ? '' : _ref$className,
      _ref$direction = _ref.direction,
      direction = _ref$direction === void 0 ? 'below' : _ref$direction,
      _ref$disabled = _ref.disabled,
      disabled = _ref$disabled === void 0 ? false : _ref$disabled,
      _ref$dispatch = _ref.dispatch,
      dispatch = _ref$dispatch === void 0 ? function () {} : _ref$dispatch,
      id = _ref.id,
      _ref$options = _ref.options,
      options = _ref$options === void 0 ? [] : _ref$options,
      selected = _ref.selected,
      _ref$type = _ref.type,
      type = _ref$type === void 0 ? 'submit' : _ref$type; // We will keep track of dropdown menu expanded state internally.

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isExpanded = _useState2[0],
      setIsExpanded = _useState2[1];

  var toggleIsExpanded = function toggleIsExpanded() {
    setIsExpanded(!isExpanded);
  }; // Close expanded menu on outside click.


  var node = useRef();
  useOutsideClick({
    element: node.current,
    onOutsideClick: function onOutsideClick() {
      return setIsExpanded(false);
    }
  }); // When a option is selected by user,
  // report out and close the dropdown.

  var handleOptionSelected = function handleOptionSelected(option) {
    return function (jsEvent) {
      dispatch({
        type: "".concat(id, "-dropdown-option-selected"),
        payload: {
          id: id,
          option: option
        }
      });
      setIsExpanded(false);
    };
  };

  var Arrow = styles.Arrow,
      Button = styles.Button,
      Menu = styles.Menu,
      MenuItem = styles.MenuItem;
  return (
    /*#__PURE__*/
    React.createElement(Button, {
      className: className,
      type: type,
      "data-testid": "".concat(id, "-dropdown-button"),
      disabled: disabled,
      ref: node,
      onClick: disabled ? undefined : toggleIsExpanded
    }, selected,
    /*#__PURE__*/
    React.createElement(Arrow, {
      isExpanded: isExpanded
    }), isExpanded &&
    /*#__PURE__*/
    React.createElement(Menu, {
      "data-testid": "".concat(id, "-dropdown-menu"),
      direction: direction
    }, options.map(function (option, index) {
      var label = option.label,
          disabled = option.disabled;
      return (
        /*#__PURE__*/
        React.createElement(MenuItem, {
          key: "".concat(label, "-index"),
          onClick: handleOptionSelected(option),
          disabled: disabled
        }, label)
      );
    })))
  );
}

Dropdown.propTypes = {
  /** Allows extending styles with styled(Dropdown) constructor. */
  className: PropTypes.string,

  /** Allows changing the type of the button. */
  type: PropTypes.string,

  /** Whether the menu should open above or below the button */
  direction: PropTypes.oneOf(['above', 'below']),

  /** When true, disabled styles are applied and the control becomes non-interactive */
  disabled: PropTypes.bool,

  /** For reporting option selected events up to parents */
  dispatch: PropTypes.func,

  /** An internal identifier. Not user visible.  */
  id: PropTypes.string.isRequired,

  /** Ordered array of options */
  options: PropTypes.arrayOf(PropTypes.shape({
    /** The displayed text label for the option */
    label: PropTypes.string,

    /** A value associated with the option.
     * This value is not user visible.
     */
    value: PropTypes.any,

    /** An optional disabled boolean value */
    disabled: PropTypes.bool
  })),

  /** The currently selected option label */
  selected: PropTypes.string.isRequired
};
var styles = {
  Button: styled.button(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n    ", ";\n\n    width: 100%; /* fluid width */\n\n    display: flex;\n    align-items: center;\n    justify-content: space-between;\n\n    appearance: none;\n    background-color: ", ";\n    border: 1px solid ", ";\n    border-radius: 4px;\n    cursor: pointer;\n    outline: none;\n    padding: 1.0625rem 1.1875rem 1.0625rem 0.75rem;\n    position: relative;\n\n    ", "\n  "])), BodyLargeStyles, surfaceM, grayMediumM, function (props) {
    return props.disabled && "\n      background-color: ".concat(grayLightM, ";\n      border-color: ").concat(grayLightM, ";\n      color: ").concat(textDisabledM, ";\n      cursor: auto;\n    ");
  }),
  Arrow: styled(CaretDown)(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n    transform: rotateZ(0deg);\n    transition: transform 100ms;\n\n    ", "\n  "])), function (props) {
    return props.isExpanded && "\n      transform: rotateZ(180deg);\n    ";
  }),
  Menu: styled.div(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n    position: absolute;\n    ", ": calc(100% + 1px);\n    left: -1px; /** ensures menu is full button width including border */\n    right: -1px;\n    z-index: 1;\n\n    display: flex;\n    flex-direction: column;\n\n    background-color: ", ";\n    border-radius: 4px;\n\n    box-shadow: 0px 10px 20px rgba(0, 0, 0, 0.04),\n    0px 2px 6px rgba(0, 0, 0, 0.04),\n    0px 0px 1px rgba(0, 0, 0, 0.04);\n\n    /** Add scrolling when menu gets too tall. */\n    max-height: 20rem;\n    overflow-x: hidden;\n    overflow-y: auto;\n  "])), function (p) {
    return p.direction === 'below' ? 'top' : 'bottom';
  }, surfaceM),
  MenuItem: styled.p(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n    ", ";\n\n    cursor: pointer;\n    padding: 0.75rem;\n    text-align: left;\n    width: 100%;\n\n    ", "\n\n    @media (hover: hover) {\n      &:hover {\n        color: ", ";\n      }\n    }\n  "])), BodyLargeStyles, function (p) {
    return p.disabled && "\n      background-color: ".concat(grayLightM, ";\n      color: ").concat(textDisabledM, ";\n      cursor: auto;\n    ");
  }, function (p) {
    return !p.disabled && primaryHighlightM;
  })
};
export default Dropdown;
export { styles };